import { useState, useEffect, useCallback, memo, useRef } from "react"
import { useDebounce } from "../hooks/useDebounce"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import CustomersOnlyCombobox from "@hydrologiq/shared/components/activities/inputs/CustomersOnlyCombobox"
import CustomerSitesCombobox from "@hydrologiq/shared/components/activities/inputs/CustomerSitesCombobox"
import { Input, VStack, HStack, Link } from "@chakra-ui/react"
import { DateTime, utc, zone } from "timezonecomplete"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast } from "../utils/toast"
import { useOrderWorkflowState, useOrderWorkflowActions, useCustomer, useSite } from "../contexts/OrderWorkflowContext"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { NativeSelectRoot, NativeSelectField } from "@hydrologiq/shared/chakra/NativeSelect"
import { CollectionSection, validateCollectionInput } from "./CollectionSection"

type ScreenId = "order" | "create-customer" | "create-site" | "create-filling-site"

interface OrderFormContainerProps {
  onSuccess: () => void
  onCancel: () => void
  onNavigateToScreen: (screenId: ScreenId) => void
}

const OrderFormContainer = memo(({ onSuccess, onCancel, onNavigateToScreen }: OrderFormContainerProps) => {
  const { start, end, name } = useOrderWorkflowState()
  const customer = useCustomer()
  const site = useSite()

  const copy = useCopyValues({
    [COPY_KEYS.FORMS.HELPER_TEXT_ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.HELPER_TEXT_ORDER],
    [COPY_KEYS.FORMS.PLEASE_SELECT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLEASE_SELECT],
    [COPY_KEYS.FORMS.END_DATE_BEFORE_START]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.END_DATE_BEFORE_START],
    [COPY_KEYS.FORMS.INVALID_DATE_FORMAT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.INVALID_DATE_FORMAT],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.LABELS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.CUSTOMER],
    [COPY_KEYS.LABELS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.SITE],
    [COPY_KEYS.LABELS.ORDER_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.ORDER_NAME],
    [COPY_KEYS.BUTTONS.CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.CREATE],
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
  })

  const { selectCustomer, selectSite, updateOrderDates, updateOrderName, updateOrderQuantity, setPendingCustomerName } =
    useOrderWorkflowActions()

  // Only manage local UI state, not form data
  const [dateValidationError, setDateValidationError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastChangedField, setLastChangedField] = useState<"start" | "end" | null>(null)
  const [orderType, setOrderType] = useState<"spot" | "vmi">("spot")
  const [collectionReplacement, setCollectionReplacement] = useState<boolean>(true)
  const [collectionQuantityCollected, setCollectionQuantityCollected] = useState<string>("")
  const [collectionQuantityError, setCollectionQuantityError] = useState<string | null>(null)

  // Local state for immediate date input values
  const [localStart, setLocalStart] = useState(start)
  const [localEnd, setLocalEnd] = useState(end)

  // Debounce the date values to prevent excessive API calls
  const debouncedStart = useDebounce(localStart, 250)
  const debouncedEnd = useDebounce(localEnd, 250)

  const [createOrder, { loading }] = useMutationWithEndpoint(
    gqlTypes.CreateOrderDocument,
    {
      // Refetch queries to update the table - Apollo cache normalization alone won't add to query results
      refetchQueries: ["GetOrders"],
      awaitRefetchQueries: false, // Don't wait for refetch to complete
    },
    "primary"
  )

  // Sync local state when context dates change externally
  useEffect(() => {
    setLocalStart(start)
    setLocalEnd(end)
  }, [start, end])

  // Sync debounced values to context
  useEffect(() => {
    if (debouncedStart !== start || debouncedEnd !== end) {
      updateOrderDates(debouncedStart, debouncedEnd)
    }
  }, [debouncedStart, debouncedEnd, start, end, updateOrderDates])

  // Validate dates whenever they change
  useEffect(() => {
    if (start && end) {
      try {
        const tz = zone(IANATimeZone())
        const startDate = new DateTime(start, "YYYY-MM-DD", tz)
        const endDate = new DateTime(end, "YYYY-MM-DD", tz)

        if (endDate.valueOf() < startDate.valueOf()) {
          setDateValidationError("End date cannot be before start date")
        } else {
          setDateValidationError(null)
        }
      } catch {
        setDateValidationError(null)
      }
    } else {
      setDateValidationError(null)
    }
  }, [start, end])

  // When customer changes, clear the selected site and auto-fill if customer has exactly one site
  const prevCustomerRef = useRef(customer)
  useEffect(() => {
    if (prevCustomerRef.current?.id !== customer?.id) {
      if (customer && customer.sites && customer.sites.length === 1) {
        // Auto-fill the single site
        selectSite({ id: customer.sites[0].id, name: customer.sites[0].name })
      } else {
        // Clear site for multiple sites or no sites
        selectSite(undefined)
      }
      prevCustomerRef.current = customer
    }
  }, [customer?.id, customer?.sites, selectSite])

  const handleNavigate = useCallback(
    (screenId: ScreenId) => {
      onNavigateToScreen(screenId)
    },
    [onNavigateToScreen]
  )

  return (
    <Form
      helperText={copy[COPY_KEYS.FORMS.HELPER_TEXT_ORDER]}
      actionType="create"
      loading={loading}
      onSubmit={(async (data: FormData) => {
        try {
          setError(null)
          const start = data.get("start") as string
          const end = data.get("end") as string

          // Validation
          const missingFields = []
          if (!customer) missingFields.push(copy[COPY_KEYS.LABELS.CUSTOMER].toLowerCase())
          if (!site) missingFields.push(copy[COPY_KEYS.LABELS.SITE].toLowerCase())
          if (!start) missingFields.push("start date")
          if (orderType === "spot" && !end) missingFields.push("end date")

          if (missingFields.length > 0) {
            const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_SELECT]}: ${missingFields.join(", ")}`
            setError(errorMessage)
            showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
            return false
          }

          if (dateValidationError) {
            setError(dateValidationError)
            showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], dateValidationError)
            return false
          }

          const tz = zone(IANATimeZone())
          let startDateTime: DateTime
          let endDateTime: DateTime | undefined

          try {
            startDateTime = new DateTime(start, "YYYY-MM-DD", tz).toZone(utc())

            if (orderType === "spot" && end) {
              endDateTime = new DateTime(end, "YYYY-MM-DD", tz).toZone(utc())

              if (endDateTime.valueOf() < startDateTime.valueOf()) {
                const errorMessage = copy[COPY_KEYS.FORMS.END_DATE_BEFORE_START]
                setError(errorMessage)
                showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
                return false
              }
            }
          } catch (error) {
            const errorMessage = copy[COPY_KEYS.FORMS.INVALID_DATE_FORMAT]
            setError(errorMessage)
            showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
            return false
          }

          const orderName = data.get("orderName") as string
          const trimmedName = orderName?.trim()

          let vars: any = {
            type: orderType,
            sites: [{ site: site.id, customer: customer.id }],
            start: startDateTime.toIsoString(),
            assets: [], // Initialise with empty assets array (required field)
          }

          if (endDateTime) {
            vars.end = endDateTime.toIsoString()
          }

          if (trimmedName) {
            vars.name = trimmedName
          }

          const quantityInput = data.get("quantity") as string
          if (quantityInput && quantityInput.trim()) {
            const quantity = parseFloat(quantityInput)
            if (isNaN(quantity) || quantity < 0) {
              const errorMessage = "Quantity must be a positive number"
              setError(errorMessage)
              showErrorToast("Validation Error", errorMessage)
              return false
            }
            vars.quantity = quantity
          }

          // Validate and include collection fields
          const collectionValidationError = validateCollectionInput(collectionReplacement, collectionQuantityCollected)
          if (collectionValidationError) {
            setCollectionQuantityError(collectionValidationError)
            return false
          }
          vars.collection_replacement = collectionReplacement
          if (collectionReplacement) {
            vars.quantity_collected = null
          } else if (collectionQuantityCollected !== "") {
            vars.quantity_collected = parseFloat(collectionQuantityCollected)
          }

          const { data: orderData } = await createOrder({
            variables: vars,
          })

          if (orderData?.createOrder) {
            showSuccessToast(
              "Order Scheduled",
              `Order "${orderData.createOrder.name || "Unnamed"}" has been successfully scheduled.`
            )
            onSuccess()
            setError(null)
            return true
          } else {
            const errorMessage = "Failed to create order. Please try again."
            setError(errorMessage)
            showErrorToast("Creation Failed", errorMessage)
            return false
          }
        } catch (error: any) {
          const errorMessage = error instanceof Error ? error.message : ""
          const statusCode = error?.networkError?.statusCode || error?.graphQLErrors?.[0]?.extensions?.code
          const responseBody = error?.networkError?.result?.detail || error?.networkError?.bodyText || ""

          if (
            statusCode === 409 ||
            errorMessage.includes("already exists") ||
            errorMessage.includes("Conflict") ||
            responseBody.includes("already exists")
          ) {
            const duplicateError = "An order with this ID already exists. Please choose a different ID."
            setError(duplicateError)
            showErrorToast("An order with this ID already exists")
          } else {
            setError("Unable to create order. Please check your inputs and try again.")
            showErrorToast("Creation Failed", "Unable to create order. Please try again.")
          }
          return false
        }
      }).bind(this)}
      onCancel={() => {
        setError(null)
        onCancel()
      }}
    >
      <VStack gap={5}>
        {error && <ErrorAlert title="Order Creation Error" message={error} onClose={() => setError(null)} />}
        {dateValidationError && (
          <ErrorAlert
            title="Validation Error"
            message={dateValidationError}
            onClose={() => setDateValidationError(null)}
          />
        )}
        <Field label="Order Type" required>
          <NativeSelectRoot>
            <NativeSelectField
              value={orderType}
              onChange={(e) => setOrderType(e.target.value as "spot" | "vmi")}
            >
              <option value="spot">Spot</option>
              <option value="vmi">VMI</option>
            </NativeSelectField>
          </NativeSelectRoot>
        </Field>
        <Field label={`${copy[COPY_KEYS.LABELS.ORDER_NAME]} (optional)`}>
          <Input
            name="orderName"
            type="text"
            onChange={(e) => {
              updateOrderName(e.target.value)
            }}
            defaultValue={name}
            placeholder="Auto-generated if not provided"
          />
        </Field>
        <HStack gap={2} width="100%">
          <Field label="Start" required>
            <Input
              name="start"
              type="date"
              value={localStart}
              onChange={(e) => {
                const newStartDate = e.target.value
                setLocalStart(newStartDate)
                setLastChangedField("start")

                // Auto-adjust end date if new start > current end
                if (newStartDate && localEnd) {
                  try {
                    const tz = zone(IANATimeZone())
                    const start = new DateTime(newStartDate, "YYYY-MM-DD", tz)
                    const end = new DateTime(localEnd, "YYYY-MM-DD", tz)

                    if (start.valueOf() > end.valueOf()) {
                      setLocalEnd(newStartDate)
                      setDateValidationError(null)
                    }
                  } catch {
                    // Invalid date format, ignore
                  }
                }
              }}
              onBlur={() => {
                // Validate on blur
                if (localStart && localEnd) {
                  try {
                    const tz = zone(IANATimeZone())
                    const startDate = new DateTime(localStart, "YYYY-MM-DD", tz)
                    const endDate = new DateTime(localEnd, "YYYY-MM-DD", tz)

                    if (endDate.valueOf() < startDate.valueOf()) {
                      // Only show validation error if end was the last changed field
                      if (lastChangedField === "end") {
                        setDateValidationError("End date cannot be before start date")
                      }
                    } else {
                      setDateValidationError(null)
                    }
                  } catch {
                    setDateValidationError(null)
                  }
                }
              }}
            />
          </Field>
          <Field label="End" required={orderType === "spot"}>
            <Input
              name="end"
              type="date"
              value={localEnd}
              onChange={(e) => {
                setLocalEnd(e.target.value)
                setLastChangedField("end")
              }}
              onBlur={() => {
                // Validate on blur
                if (localStart && localEnd) {
                  try {
                    const tz = zone(IANATimeZone())
                    const startDate = new DateTime(localStart, "YYYY-MM-DD", tz)
                    const endDate = new DateTime(localEnd, "YYYY-MM-DD", tz)

                    if (endDate.valueOf() < startDate.valueOf()) {
                      // Only show validation error if end was the last changed field
                      if (lastChangedField === "end") {
                        setDateValidationError("End date cannot be before start date")
                      }
                    } else {
                      setDateValidationError(null)
                    }
                  } catch {
                    setDateValidationError(null)
                  }
                }
              }}
            />
          </Field>
        </HStack>
        <Field label={copy[COPY_KEYS.LABELS.CUSTOMER]} required>
          <CustomersOnlyCombobox
            onItemSelection={(customer) => customer && selectCustomer(customer)}
            initialValue={customer}
            onInputChange={setPendingCustomerName}
            CreateAction={() => (
              <Link onClick={() => handleNavigate("create-customer")} variant="primary">
                Create {copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()}
              </Link>
            )}
          />
        </Field>
        <Field label={copy[COPY_KEYS.LABELS.SITE]} required>
          <CustomerSitesCombobox
            customer={customer}
            onItemSelection={(site) => site && selectSite(site)}
            initialValue={site}
            CreateAction={() =>
              customer ? (
                <Link onClick={() => handleNavigate("create-site")} variant="primary">
                  Create site
                </Link>
              ) : undefined
            }
          />
        </Field>

        <Field label="Delivery quantity (kg)">
          <Input
            name="quantity"
            type="number"
            min={0}
            step="0.01"
            placeholder="Optional"
            onChange={(e) => updateOrderQuantity(parseFloat(e.target.value) || 0)}
          />
        </Field>

        <CollectionSection
          collectionReplacement={collectionReplacement}
          quantityCollected={collectionQuantityCollected}
          onCollectionReplacementChange={(value) => {
            setCollectionReplacement(value)
            if (value) {
              setCollectionQuantityCollected("")
              setCollectionQuantityError(null)
            }
          }}
          onQuantityCollectedChange={(value) => {
            setCollectionQuantityCollected(value)
            if (collectionQuantityError) setCollectionQuantityError(null)
          }}
          quantityCollectedError={collectionQuantityError}
        />
      </VStack>
    </Form>
  )
})

OrderFormContainer.displayName = "OrderFormContainer"

export default OrderFormContainer
